/* ============================================================================
   RESPALDO DEL LIBRO DE VUELO

   Los datos viven en IndexedDB del dispositivo. **Un piloto que pierde el iPad
   pierde su libro de vuelo**, y un libro de vuelo es el respaldo documental de
   una licencia: sin él no se puede demostrar experiencia ni currency.

   ══════════════════════════════════════════════════════════════════════════
   POR QUÉ SE INSISTE Y NO SE PIDE UNA VEZ

   Un aviso que aparece una vez se ignora. Lo que protege un libro no es
   recordar que hay que respaldarlo: es que la aplicación **no deje olvidarlo**.

   El aviso reaparece y se vuelve más insistente con los días. No se puede
   apagar para siempre: solo posponer, y posponerlo queda registrado.

   ══════════════════════════════════════════════════════════════════════════
   LO QUE ESTO NO ES

   No es una copia en la nube. El archivo lo guarda el operador donde quiera —
   correo, disco, carpeta compartida— y la aplicación **no sabe si llegó a
   algún lado**. Por eso registra que se exportó, no que se respaldó.

   Decir «respaldado» cuando solo se descargó un archivo sería afirmar algo que
   la aplicación no puede saber.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

/* Las columnas y el formato de horas salen del libro, no de una lista propia:
   si mañana el libro agrega una columna, la planilla la tiene sola. */
import { COLUMNAS_LIBRO, aHhMm, etiquetaDeEntrada } from "./bitacoraPiloto.js";

const DIA_MS = 86400000;

/** Número, o null si no hay dato. Nunca 0 por falta de dato. */
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Cada cuántos días hay que exportar. */
export const DIAS_ENTRE_RESPALDOS = 7;

/** A partir de acá el aviso se vuelve serio. */
export const DIAS_PARA_ALARMA = 14;

/** Dónde se recuerda la última exportación. */
export const CLAVE_ULTIMO = "hcUltimoRespaldo";

export function ultimoRespaldo() {
  try {
    const t = localStorage.getItem(CLAVE_ULTIMO);
    const ms = Date.parse(t || "");
    return Number.isFinite(ms) ? t : null;
  } catch { return null; }
}

export function registrarRespaldo(cuando = new Date()) {
  try {
    localStorage.setItem(CLAVE_ULTIMO, cuando.toISOString());
  } catch { /* modo privado: no se puede recordar, y el aviso va a insistir */ }
}

/**
 * Estado del respaldo.
 *
 * `asientos` es cuántos hay en el libro: **con el libro vacío no se avisa
 * nada**. Pedir un respaldo de cero asientos enseña a ignorar el aviso antes
 * de que haya algo que perder.
 */
export function estadoDelRespaldo({ asientos = 0, ahora = new Date() } = {}) {
  const cuantos = Number(asientos) || 0;

  if (cuantos === 0) {
    return { avisa: false, nivel: "ninguno", dias: null, motivo: null };
  }

  const ultimo = ultimoRespaldo();

  if (!ultimo) {
    /* Nunca se exportó. No se sabe hace cuánto: se sabe que **nunca**, que es
       peor. */
    return {
      avisa: true,
      nivel: cuantos >= 20 ? "alarma" : "aviso",
      dias: null,
      nunca: true,
      motivo: `El libro tiene ${cuantos} ${cuantos === 1 ? "asiento" : "asientos"} `
        + "y nunca se exportó. Si pierdes el dispositivo, se pierden.",
    };
  }

  const dias = Math.floor((ahora.getTime() - Date.parse(ultimo)) / DIA_MS);

  if (dias < DIAS_ENTRE_RESPALDOS) {
    return { avisa: false, nivel: "ninguno", dias, motivo: null };
  }

  const alarma = dias >= DIAS_PARA_ALARMA;

  return {
    avisa: true,
    nivel: alarma ? "alarma" : "aviso",
    dias,
    nunca: false,
    motivo: `Hace ${dias} días que no exportas el libro. `
      + (alarma
        ? "Un libro de vuelo es el respaldo de tu licencia: sin él no puedes "
          + "demostrar experiencia ni currency."
        : "Exportalo y guarda el archivo fuera del dispositivo."),
  };
}

/**
 * Posponer.
 *
 * NO se puede apagar para siempre. Se corre tres días, y queda registrado
 * cuántas veces: un piloto que pospone cinco veces seguidas tiene un problema
 * que el aviso no va a resolver, y el operador debería saberlo.
 */
export const CLAVE_POSPUESTO = "hcRespaldoPospuesto";
export const DIAS_POSPONER = 3;

export function posponer(ahora = new Date()) {
  try {
    const previo = JSON.parse(localStorage.getItem(CLAVE_POSPUESTO) || "null");
    localStorage.setItem(CLAVE_POSPUESTO, JSON.stringify({
      hasta: new Date(ahora.getTime() + DIAS_POSPONER * DIA_MS).toISOString(),
      veces: (previo?.veces || 0) + 1,
    }));
  } catch { /* modo privado */ }
}

/** ¿Está pospuesto ahora mismo? */
export function estaPospuesto(ahora = new Date()) {
  try {
    const p = JSON.parse(localStorage.getItem(CLAVE_POSPUESTO) || "null");
    if (!p?.hasta) return { pospuesto: false, veces: 0 };

    const vence = Date.parse(p.hasta);
    return {
      pospuesto: Number.isFinite(vence) && ahora.getTime() < vence,
      veces: p.veces || 0,
    };
  } catch { return { pospuesto: false, veces: 0 }; }
}

/**
 * ¿Hay que mostrar el aviso ahora?
 *
 * La alarma **ignora el posponer**: a los catorce días sin exportar, correrlo
 * tres días más es lo que hace que un libro se pierda.
 */
export function hayQueAvisar({ asientos = 0, ahora = new Date() } = {}) {
  const estado = estadoDelRespaldo({ asientos, ahora });
  if (!estado.avisa) return { mostrar: false, estado };

  const { pospuesto, veces } = estaPospuesto(ahora);

  if (estado.nivel === "alarma") {
    return { mostrar: true, estado, veces, sePuedePosponer: false };
  }

  return { mostrar: !pospuesto, estado, veces, sePuedePosponer: true };
}

/** Nombre del archivo: la fecha adelante, para que ordenen solos. */
export function nombreDeArchivo({ piloto = "", ahora = new Date(), extension = "json" } = {}) {
  const fecha = ahora.toISOString().slice(0, 10);
  const quien = String(piloto).trim().replace(/\s+/g, "-").toLowerCase();
  const base = quien ? `libro-${fecha}-${quien}` : `libro-${fecha}`;
  return `${base}.${String(extension).replace(/^\./, "")}`;
}

/* ══════════════════════════════════════════════════════════════════════════
   LO QUE SE EXPORTA, Y CÓMO SE LLAMA

   ACÁ VIVE EL RESPALDO ENTERO, Y ESO ES EL PUNTO

   Hasta la e118 había tres definiciones distintas del mismo respaldo: esta
   —que sabía cuándo insistir y no lo llamaba nadie—, la pantalla del libro
   —que armaba su propio paquete con su propio nombre de archivo— y una
   función suelta en `services/vuelos/registroVuelos.js` con un tercer formato.

   Tres definiciones del mismo respaldo es el mismo error que los dos
   cronómetros y las dos verdades del FT/AT: cuando discrepan no se sabe cuál
   creer, y el que paga es el libro del piloto.

   Ahora la pantalla NO decide qué se exporta: pide el paquete acá y lo baja.
   Lo único que queda del lado de la pantalla es el navegador —crear el blob,
   disparar la descarga—, que es lo que un módulo de dominio no puede hacer.

   DE QUÉ FORMATO, Y POR QUÉ DOS

     JSON     es el respaldo completo: los asientos con su trazabilidad, los
              totales, los períodos y el currency. Es lo que se restaura y lo
              que se presenta ante una autoridad.
     XLSX     es la planilla para ARCHIVAR E IMPRIMIR: una fila por vuelo y
              las columnas del libro en el orden del libro en papel. No lleva
              más datos que el JSON, pero se lee y se firma sin una computadora.
              Un respaldo que solo se puede leer en la aplicación que lo generó
              no sirve como respaldo documental.
   ══════════════════════════════════════════════════════════════════════════ */

/** Identifica el archivo como nuestro. Permite rechazar otro formato al abrir. */
export const FORMATO = "helicalc-libro-vuelo";

/** La versión del formato. Si cambia la forma del archivo, cambia esto. */
export const VERSION = 1;

/**
 * Las palabras que ve el piloto.
 *
 * Se exportan para que se puedan comprobar: una prueba que mira el código
 * comprueba que alguien escribió una frase, no que la aplicación la diga. Y se
 * escriben acá, con el resto del respaldo, porque la decisión de no decir
 * «respaldado» es una decisión del respaldo.
 */
export const TEXTOS = {
  accion: "Exportar el libro",
  accionPlanilla: "Planilla para archivar o imprimir (Excel)",
  ayuda: "El archivo lo guardas tú donde quieras: correo, disco, carpeta "
    + "compartida. La aplicación no sabe si llegó a algún lado, así que queda "
    + "registrado que se exportó, no que se respaldó.",
  posponer: "Recordarme en tres días",
  alarmaNoSePospone: "A esta altura el aviso no se puede posponer: un libro de "
    + "vuelo es el respaldo documental de una licencia.",
};

/**
 * El respaldo en JSON, listo para bajar.
 *
 * @returns {{nombre: string, tipo: string, texto: string}}
 */
export function paqueteDeRespaldo({
  asientos = [], piloto = "", totales = null, periodos = [], currency = null, ahora = new Date(),
} = {}) {
  const contenido = {
    formato: FORMATO,
    version: VERSION,
    piloto: piloto || null,
    generado: ahora.toISOString(),
    totales,
    periodos,
    currency,
    asientos,
  };
  return {
    nombre: nombreDeArchivo({ piloto, ahora }),
    tipo: "application/json",
    texto: JSON.stringify(contenido, null, 2),
  };
}

/* Las columnas que identifican cada vuelo y que en el libro en papel van a la
   izquierda de las horas. No se inventan acá: son los campos del asiento tal
   como los produce `bitacoraPiloto.js`. */
const IDENTIFICADORAS = [
  ["fecha", "Fecha"],
  ["entrada", "Entrada"],
  ["tipoAeronave", "Aeronave"],
  ["matricula", "Matrícula"],
  ["rutaDesde", "Desde"],
  ["rutaHasta", "Hasta"],
];

const valorDeCelda = (asiento, columna) => {
  const v = asiento?.[columna.clave];
  if (columna.tipo === "horas") return aHhMm(v) ?? "";
  if (columna.tipo === "entero") {
    const n = num(v);
    return n === null ? "" : n;
  }
  return v ?? "";
};

/**
 * La planilla del libro, para archivar e imprimir.
 *
 * Devuelve los DATOS, no el archivo: el módulo de dominio no sabe de Excel ni
 * de `xlsx`, igual que no sabe de blobs ni de descargas. La pantalla convierte
 * esto en un `.xlsx` y lo baja.
 *
 * Las columnas son `COLUMNAS_LIBRO` —las mismas que usa la pantalla y el
 * formulario—, así que una columna nueva del libro aparece sola en la planilla
 * en vez de quedar olvidada en el archivo que se firma.
 *
 * `totales` es el objeto que ya calcula `totalesDeLibro()`: no se vuelve a
 * sumar acá, porque dos implementaciones de la misma suma terminan
 * discrepando, y la que se firma es la planilla.
 */
export function planillaDelLibro({
  asientos = [], piloto = "", totales = null, ahora = new Date(),
} = {}) {
  const lista = Array.isArray(asientos) ? asientos : [];
  const columnas = COLUMNAS_LIBRO.map((c) => ({ clave: c.clave, etiqueta: c.etiqueta, tipo: c.tipo }));

  const encabezados = [
    ...IDENTIFICADORAS.map(([, titulo]) => titulo),
    ...columnas.map((c) => c.etiqueta),
  ];

  const filas = lista.map((a) => [
    ...IDENTIFICADORAS.map(([clave]) => (
      clave === "entrada" ? (etiquetaDeEntrada(a) ?? "") : (a?.[clave] ?? "")
    )),
    ...columnas.map((c) => valorDeCelda(a, c)),
  ]);

  const filaTotales = totales === null ? null : [
    ...IDENTIFICADORAS.map(([clave]) => (clave === "fecha" ? "TOTAL" : "")),
    ...columnas.map((c) => {
      const v = totales?.[c.clave];
      if (c.tipo === "horas") return aHhMm(v) ?? "";
      return num(v) ?? "";
    }),
  ];

  return {
    nombre: nombreDeArchivo({ piloto, ahora, extension: "xlsx" }),
    /* Lo que va arriba de la tabla en la planilla impresa: sin esto, una hoja
       suelta no dice de quién es ni cuándo se generó. */
    encabezado: {
      titulo: `Libro de vuelo${piloto ? ` — ${piloto}` : ""}`,
      generado: ahora.toISOString().slice(0, 16).replace("T", " "),
      aviso: "Registro generado por HeliCalc, herramienta de apoyo a la decisión.",
    },
    encabezados,
    filas,
    filaTotales,
  };
}
